import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import s from "./RowKebab.module.css";

/**
 * Row-level kebab menu. Portals the dropdown to document.body so it
 * isn't clipped by parent overflow:hidden, and re-anchors via the
 * trigger button's bounding rect. Auto-flips upward when there isn't
 * enough space below the trigger; closes on outside click, ESC,
 * scroll (any nested container), and resize.
 *
 * Props:
 *   items       Array<{ label, onClick, danger? }>
 *   ariaLabel   accessible label for the trigger (default: "More actions")
 */
export default function RowKebab({ items, ariaLabel = "More actions" }) {
  const btnRef  = useRef(null);
  const menuRef = useRef(null);
  // null = closed; { top, right, placement } = open at viewport coords.
  const [coords, setCoords] = useState(null);
  const open = !!coords;

  function placeAndOpen() {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    // Generous estimate: 12px padding + 40px per item.
    const estHeight = 12 + items.length * 40;
    const spaceBelow = window.innerHeight - r.bottom;
    const placement = spaceBelow < estHeight + 12 && r.top > estHeight + 12 ? "up" : "down";
    setCoords({
      top: placement === "down" ? r.bottom + 6 : Math.max(8, r.top - estHeight - 6),
      right: Math.max(8, window.innerWidth - r.right),
      placement,
    });
  }
  function close() { setCoords(null); }

  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (btnRef.current?.contains(e.target))  return;
      if (menuRef.current?.contains(e.target)) return;
      close();
    }
    function onKey(e) { if (e.key === "Escape") close(); }
    // Close on any scroll (incl. nested scroll containers) — the
    // anchored coords would otherwise drift away from the trigger.
    function onScroll() { close(); }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={s.kebabBtn}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => (open ? close() : placeAndOpen())}>
        ⋯
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          className={s.kebabMenu}
          style={{ top: coords.top, right: coords.right }}>
          {items.map((it, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              className={it.danger ? s.kebabItemDanger : s.kebabItem}
              onClick={() => { close(); it.onClick(); }}>
              {it.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
